<script lang="ts">
  import { push } from 'svelte-spa-router';
  import Loading from '../../etc/loader.svelte';
  import { loginUser } from '../../components/auth/LoginUser';
  import { getContext } from 'svelte';
  import NotesPenaltyModal from '../../etc/modal/hikari01/withdrawal/NotesPenaltyModal.svelte';
  import NotesCampaignPenaltyModal from '../../etc/modal/hikari01/withdrawal/NotesCampaignPenaltyModal.svelte';
  import NotesEquipmentRemovalWorkModal from '../../etc/modal/hikari01/withdrawal/NotesEquipmentRemovalWorkModal.svelte';
  import NotesRemainingConstructionCostModal from '../../etc/modal/hikari01/withdrawal/NotesRemainingConstructionCostModal.svelte';
  import NotesReturnOfLineModemModal from '../../etc/modal/hikari01/withdrawal/NotesReturnOfLineModemModal.svelte';
  import NotesWithdrawalOfTelModal from '../../etc/modal/hikari01/withdrawal/NotesWithdrawalOfTelModal.svelte';
  import NotesWithdrawalOfGateCallModal from '../../etc/modal/hikari01/withdrawal/NotesWithdrawalOfGateCallModal.svelte';
  import NotesMailAddressModal from '../../etc/modal/hikari01/withdrawal/NotesMailAddressModal.svelte';
  import WithdrawalCompleteModal from '../../etc/modal/hikari01/withdrawal/WithdrawalCompleteModal.svelte';
  import StringUtil from '../../util/StringUtil';
  import RepositoryFactory, { CONTRACT, SUB_CONTRACT_HIKARI01_TEL } from '../../repositories/RepositoryFactory';
  import type { GetContractResult, FetchHikari01PenaltyResult } from '../../repositories/contract';
  import type { SubContractHikari01TelResult } from '../../repositories/subcontracthikari01tel';
  import { getDate, endOfMonth, format } from 'date-fns';
  import type { INotesItem, IBaseInfoItem, IEquipmentRemovalWorkItem } from './types';

  const { open, close }: any = getContext('simple-modal');

  let contract: GetContractResult;
  let hikari01Penalty: FetchHikari01PenaltyResult;
  let hikari01Tel: SubContractHikari01TelResult = null; // 1アカウントに対して必ず 1件 or null

  const errorProcess = (error: any) => {
    if (error.response.status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      push('/login');
    } else {
      alert('エラーが発生しました。');
    }
  };

  let promise = initialize();

  /** 初期表示時処理 */
  async function initialize() {
    const contractRepository = RepositoryFactory[CONTRACT];

    await Promise.all([contractRepository.getContract(), contractRepository.fetchHikari01Penalty()])
      .then((response) => {
        contract = response[0];
        hikari01Penalty = response[1];
        fetchSubContractHikari01Tel(hikari01Penalty);
      })
      .catch((error) => errorProcess(error));

    setValuesToBaseInfo();
    setValuesToNotesPenalty();
    setValuesToNotesCampaignPenalty();
    setValuesToNotesRemainingConstructionCost();
    setValuesToNotesEquipmentRemovalWork();
    setValuesToNotesReturnOfLineModem();
    setValuesToNotesWithdrawalOfTel();
    setValuesToNotesWithdrawalOfGateCall();
    setValuesToNotesMailAddress();

    return { contract, hikari01Penalty, hikari01Tel };
  }

  const fetchSubContractHikari01Tel = (penalty: FetchHikari01PenaltyResult) => {
    let subContractId = getContractedSubContractHikari01TelId(penalty);
    if (!subContractId) {
      return;
    }

    const repository = RepositoryFactory[SUB_CONTRACT_HIKARI01_TEL];
    repository
      .fetch({ subContractId })
      .then((response) => {
        hikari01Tel = response;
      })
      .catch((error) => errorProcess(error));
  };

  /** Helper Logic */
  const getContractedMailAddress = () => {
    return []
      .concat(hikari01Penalty?.contractedHikari01Mail)
      .concat(hikari01Penalty?.contractedHikari01InheritedQitMail)
      .concat(hikari01Penalty?.contractedHikari01InheritedSpaaqsMail);
  };

  const isDisplayMailAddress = (): boolean => {
    return getContractedMailAddress().length > 0;
  };

  const getContractedSubContractHikari01TelId = (penalty: FetchHikari01PenaltyResult): number | null => {
    // いずれかの1契約のため、存在する場合にreturnする形とする
    if (penalty?.contractedHikari01Ienodenwa) {
      return penalty?.contractedHikari01Ienodenwa;
    }
    if (penalty?.contractedHikari01GateCallF) {
      return penalty?.contractedHikari01GateCallF;
    }
    if (penalty?.contractedHikari01GateCallM) {
      return penalty?.contractedHikari01GateCallM;
    }
    return null;
  };

  const isContractedHikari01TelIenodenwa = (): boolean => {
    return hikari01Penalty?.contractedHikari01Ienodenwa > 0;
  };

  const isContractedHikari01TelGateCall = (): boolean => {
    return hikari01Penalty?.contractedHikari01GateCallF > 0 || hikari01Penalty?.contractedHikari01GateCallM > 0;
  };

  $: buildContractedHikari01TelString = (): string => {
    if (!hikari01Tel) {
      return '';
    }
    return `${hikari01Tel.telServiceName}(電話番号:${hikari01Tel.hikari01TelNumber})`;
  };

  /** 画面設定処理 */
  // 共通情報
  let baseInfo: IBaseInfoItem;
  const setValuesToBaseInfo = () => {
    baseInfo = {
      contractName: contract?.serviceName,
      terminateDate: format(endOfMonth(new Date()), 'yyyy/MM/dd')
    };
  };

  // 違約金モーダル
  let notesPenalty: INotesItem;
  const setValuesToNotesPenalty = () => {
    notesPenalty = {
      isDisplay: hikari01Penalty?.penaltyAmount > 0,
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesPenaltyModal, {
          contract: contract,
          handleConfirmEvent: () => {
            notesPenalty.isDisabled = false;
            notesPenalty.bgColor = 'bg-yellow-100';
            close();
          }
        });
      },
      onChangeCheckbox: () => {
        notesPenalty.bgColor = notesPenalty.isChecked ? 'bg-sky-100' : 'bg-yellow-100';
      }
    };
  };

  // キャンペーン違約金モーダル
  let notesCampaignPenalty: INotesItem;
  const setValuesToNotesCampaignPenalty = () => {
    notesCampaignPenalty = {
      isDisplay: hikari01Penalty?.campaignPenaltyAmount > 0,
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesCampaignPenaltyModal, {
          handleConfirmEvent: () => {
            notesCampaignPenalty.isDisabled = false;
            notesCampaignPenalty.bgColor = 'bg-yellow-100';
            close();
          }
        });
      },
      onChangeCheckbox: () => {
        notesCampaignPenalty.bgColor = notesCampaignPenalty.isChecked ? 'bg-sky-100' : 'bg-yellow-100';
      }
    };
  };

  // 工事費残債モーダル
  let notesRemainingConstructionCost: INotesItem;
  const setValuesToNotesRemainingConstructionCost = () => {
    notesRemainingConstructionCost = {
      isDisplay: hikari01Penalty?.remainingConstructionCost > 0,
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesRemainingConstructionCostModal, {
          handleConfirmEvent: () => {
            notesRemainingConstructionCost.isDisabled = false;
            notesRemainingConstructionCost.bgColor = 'bg-yellow-100';
            close();
          }
        });
      },
      onChangeCheckbox: () => {
        notesRemainingConstructionCost.bgColor = notesRemainingConstructionCost.isChecked
          ? 'bg-sky-100'
          : 'bg-yellow-100';
      }
    };
  };

  // 設備撤去工事モーダル
  let notesEquipmentRemovalWork: INotesItem;
  let notesEquipmentRemovalWorkItem: IEquipmentRemovalWorkItem;
  const setValuesToNotesEquipmentRemovalWork = () => {
    notesEquipmentRemovalWork = {
      isDisplay: hikari01Penalty?.isOccurEquipmentRemovalWork,
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesEquipmentRemovalWorkModal, {
          handleConfirmEvent: (item: IEquipmentRemovalWorkItem) => {
            notesEquipmentRemovalWorkItem = item;
            notesEquipmentRemovalWork.isDisabled = false;
            notesEquipmentRemovalWork.bgColor = 'bg-yellow-100';
            close();
          },
          item: notesEquipmentRemovalWorkItem
        });
      },
      onChangeCheckbox: () => {
        notesEquipmentRemovalWork.bgColor = notesEquipmentRemovalWork.isChecked ? 'bg-sky-100' : 'bg-yellow-100';
      }
    };
  };

  // 回線モデムの返却モーダル
  let notesReturnOfLineModem: INotesItem;
  const setValuesToNotesReturnOfLineModem = () => {
    notesReturnOfLineModem = {
      isDisplay: hikari01Penalty?.isOccurReturnOfLineModem,
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesReturnOfLineModemModal, {
          handleConfirmEvent: () => {
            notesReturnOfLineModem.isDisabled = false;
            notesReturnOfLineModem.bgColor = 'bg-yellow-100';
            close();
          }
        });
      },
      onChangeCheckbox: () => {
        notesReturnOfLineModem.bgColor = notesReturnOfLineModem.isChecked ? 'bg-sky-100' : 'bg-yellow-100';
      }
    };
  };

  // いえのでんわの解約 モーダル
  let notesWithdrawalOfTel: INotesItem;
  const setValuesToNotesWithdrawalOfTel = () => {
    notesWithdrawalOfTel = {
      isDisplay: isContractedHikari01TelIenodenwa(),
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesWithdrawalOfTelModal, {
          hikari01Tel: hikari01Tel,
          handleConfirmEvent: () => {
            notesWithdrawalOfTel.isDisabled = false;
            notesWithdrawalOfTel.bgColor = 'bg-yellow-100';
            close();
          }
        });
      },
      onChangeCheckbox: () => {
        notesWithdrawalOfTel.bgColor = notesWithdrawalOfTel.isChecked ? 'bg-sky-100' : 'bg-yellow-100';
      }
    };
  };

  //  GateCall(F) / GateCall(M)の解約 モーダル
  let notesWithdrawalOfGateCall: INotesItem;
  const setValuesToNotesWithdrawalOfGateCall = () => {
    notesWithdrawalOfGateCall = {
      isDisplay: isContractedHikari01TelGateCall(),
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesWithdrawalOfGateCallModal, {
          hikari01Tel: hikari01Tel,
          handleConfirmEvent: () => {
            notesWithdrawalOfGateCall.isDisabled = false;
            notesWithdrawalOfGateCall.bgColor = 'bg-yellow-100';
            close();
          }
        });
      },
      onChangeCheckbox: () => {
        notesWithdrawalOfGateCall.bgColor = notesWithdrawalOfGateCall.isChecked ? 'bg-sky-100' : 'bg-yellow-100';
      }
    };
  };

  // メールアドレスについて モーダル
  let notesMailAddress: INotesItem;
  const setValuesToNotesMailAddress = () => {
    notesMailAddress = {
      isDisplay: isDisplayMailAddress(),
      isDisabled: true,
      isChecked: false,
      bgColor: 'bg-gray-100',
      showModal: () => {
        open(NotesMailAddressModal, {
          mailAddresses: getContractedMailAddress(),
          handleConfirmEvent: () => {
            notesMailAddress.isDisabled = false;
            notesMailAddress.bgColor = 'bg-yellow-100';
            close();
          }
        });
      },
      onChangeCheckbox: () => {
        notesMailAddress.bgColor = notesMailAddress.isChecked ? 'bg-sky-100' : 'bg-yellow-100';
      }
    };
  };

  // 連絡情報
  let contactTelNo: string = '';
  let contactMailAddress: string = '';
  let contactMailAddressConfirmation: string = '';

  let contactTelNoErrorMessage: string = '';
  let contactMailAddressErrorMessage: string = '';
  let contactMailAddressConfirmationErrorMessage: string = '';

  const isCarrierDomain = (value: string): boolean => {
    // 事前にisMailAddressにて@についても検証するためsplitのlength検証はしない
    return [
      'docomo.ne.jp',
      'ezweb.ne.jp',
      'au.com',
      'willcom.com',
      'y-mobile.ne.jp',
      'emobile.ne.jp',
      'ymobile1.ne.jp',
      'ymobile.ne.jp',
      'softbank.ne.jp',
      'vodafone.ne.jp',
      'i.softbank.jp',
      'disney.ne.jp'
    ].includes(value.split('@')[1]);
  };

  // 解約の手続き 確認〜完了 モーダル
  const showWithdrawalCompleteModal = () => {
    contactTelNoErrorMessage = '';
    contactMailAddressErrorMessage = '';
    contactMailAddressConfirmationErrorMessage = '';

    if (!contactTelNo) {
      return;
    }

    // 連絡先電話番号の検証
    if (!StringUtil.isTelNo(contactTelNo)) {
      contactTelNoErrorMessage = '正しい電話番号を入力してください';
      return;
    }

    // 連絡先用メールアドレスに入力がある場合のみ検証する
    if (contactMailAddress) {
      if (contactMailAddress !== contactMailAddressConfirmation) {
        contactMailAddressConfirmationErrorMessage = '連絡先メールアドレスと一致していません';
        return;
      }
      if (!StringUtil.isMailAddress(contactMailAddress)) {
        contactMailAddressErrorMessage = '正しいメールアドレスを入力してください';
        return;
      }
      if (isCarrierDomain(contactMailAddress)) {
        contactMailAddressErrorMessage = 'キャリアのメールアドレスは使用できません';
        return;
      }
    }

    open(
      WithdrawalCompleteModal,
      {
        baseInfoItem: {
          contractName: baseInfo.contractName,
          penaltyAmount: hikari01Penalty.penaltyAmount,
          campaignPenaltyAmount: hikari01Penalty.campaignPenaltyAmount,
          remainingConstructionCost: hikari01Penalty.remainingConstructionCost,
          terminateDate: baseInfo.terminateDate,
          contractTelName: hikari01Tel?.telServiceName,
          contractTelNo: hikari01Tel?.hikari01TelNumber,
          contractMailAddress: getContractedMailAddress()
        },
        notesItem: {
          // 非表示の場合は必ずfalseが設定されるので確認画面ではそのまま使用
          notesPenaltyFlg: notesPenalty.isChecked,
          notesCampaignPenaltyFlg: notesCampaignPenalty.isChecked,
          notesRemainingConstructionCostFlg: notesRemainingConstructionCost.isChecked,
          notesEquipmentRemovalWorkFlg: notesEquipmentRemovalWork.isChecked,
          notesEquipmentRemovalWorkItem: notesEquipmentRemovalWorkItem,
          notesReturnOfLineModemFlg: notesReturnOfLineModem.isChecked,
          notesWithdrawalOfTelFlg: notesWithdrawalOfTel.isChecked,
          notesWithdrawalOfGateCallFlg: notesWithdrawalOfGateCall.isChecked,
          notesMailAddressFlg: notesMailAddress.isChecked
        },
        contactItem: {
          contactTelNo,
          contactMailAddress
        },
        handleComfirmCloseEvent: () => {
          close();
        },
        handleCompleteCloseEvent: () => {
          // ステータスの更新があるため契約内容画面に遷移後クローズ
          push(`/contract/info/`);
          close();
        }
      },
      {
        closeButton: false,
        closeOnEsc: false,
        closeOnOuterClick: false
      }
    );
  };

  $: isAllNotesChecked = () => {
    // [Note]判定方法
    // 表示されている場合=チェックボックスのON/OFFで判定
    // 表示されていない場合=強制的にtrue ※AND条件を満たすために設定。遷移時の値には使用しない
    return (
      (notesPenalty.isDisplay ? notesPenalty.isChecked : true) &&
      (notesCampaignPenalty.isDisplay ? notesCampaignPenalty.isChecked : true) &&
      (notesRemainingConstructionCost.isDisplay ? notesRemainingConstructionCost.isChecked : true) &&
      (notesEquipmentRemovalWork.isDisplay ? notesEquipmentRemovalWork.isChecked : true) &&
      (notesReturnOfLineModem.isDisplay ? notesReturnOfLineModem.isChecked : true) &&
      (notesWithdrawalOfTel.isDisplay ? notesWithdrawalOfTel.isChecked : true) &&
      (notesWithdrawalOfGateCall.isDisplay ? notesWithdrawalOfGateCall.isChecked : true) &&
      (notesMailAddress.isDisplay ? notesMailAddress.isChecked : true)
    );
  };

  const isWidhdrawalOutSideThePeriod = () => {
    const currentDate: number = getDate(new Date());
    return !(1 <= currentDate && currentDate <= 20);
  };

  $: isArrowedBusinessCode = () => {
    return $loginUser?.businessCode !== 'HIKARI_01';
  };

  $: isContractTerminated = () => {
    return contract?.contractStatusCode === 'TERMINATED';
  };
</script>

{#await promise}
  <Loading />
{:then}
  <div class="mb-6">
    <div class="container flex justify-between">
      <h1 class="text-2xl font-bold text-gray-700">解約の手続き</h1>
    </div>
  </div>
  {#if isWidhdrawalOutSideThePeriod()}<!-- 解約可能期間外-->
    <div class="shadow rounded bg-white border-t-2 border-green-400 p-4">
      <div class="flex-1 m-10">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <div class="mb:font-sm w-full">
            <ul class="list-none">
              <li>
                <p>回線契約の解約は毎月20日までとなっております。</p>
              </li>
              <li>
                <p>今月の解約は締め切りましたので、次月1日以降20日までにお手続きいただけますようお願いします。</p>
              </li>
              <li class="mt-10">
                <p>お手続きについてご不明な点がございましたら、以下からご連絡いただくようお願いいたします。</p>
              </li>
              <li>
                <p>URL：https://bb01.unext.co.jp/contact</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  {:else if isArrowedBusinessCode()}<!-- 光01以外の場合 -->
    <div class="shadow rounded bg-white border-t-2 border-green-400 p-4">
      <div class="flex-1 m-10">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <div class="mb:font-sm w-full">
            <ul class="list-none">
              <li>
                <p>不正な画面遷移です。</p>
              </li>
              <li class="mt-10">
                <p>お手続きについてご不明な点がございましたら、以下からご連絡いただくようお願いいたします。</p>
              </li>
              <li>
                <p>URL：https://bb01.unext.co.jp/contact</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  {:else if isContractTerminated()}<!-- すでに解約済の場合 -->
    <div class="shadow rounded bg-white border-t-2 border-green-400 p-4">
      <div class="flex-1 m-10">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <div class="mb:font-sm w-full">
            <ul class="list-none">
              <li>
                <p>回線契約は解約されています。</p>
              </li>
              <li class="mt-10">
                <p>お手続きについてご不明な点がございましたら、以下からご連絡いただくようお願いいたします。</p>
              </li>
              <li>
                <p>URL：https://bb01.unext.co.jp/contact</p>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  {:else}<!-- 解約可能期間内 -->
    <div class="shadow rounded bg-white border-t-2 border-green-400">
      <div class="flex-1 m-10">
        <div class="inline-flex justify-items-center items-center min-w-full">
          <div class="mb:font-sm w-full">
            以下内容でお手続きを進めさせていただきます。内容を確認いただき、「手続きへ」を押下ください。
          </div>
        </div>

        <div class="inline-flex justify-items-center items-center min-w-full mt-10">
          <ul class="list-disc mb:font-sm ml-4">
            <li>
              <p>解約する回線契約プラン：{baseInfo.contractName}</p>
            </li>
            <li>
              <p>解約日：{baseInfo.terminateDate}</p>
            </li>
          </ul>
        </div>

        <div class="inline-flex justify-items-center items-center min-w-full mt-4">
          <div class="mb:font-sm mb-1 w-full">
            <ul class="list-none">
              <li>
                <p>[回線契約の解約とともに解約となるサービス]</p>
              </li>
            </ul>
            <ul class="list-disc ml-8">
              <li>
                <p>電話契約：{buildContractedHikari01TelString()}</p>
              </li>
              <li>
                <p>メールアドレス</p>
              </li>
            </ul>
            <ul class="list-none ml-4">
              <li>
                <p>上記以外のオプションサービス等も併せて解約となります</p>
              </li>
            </ul>
          </div>
        </div>

        <div class="inline-flex justify-items-center items-center min-w-full mt-10">
          <div class="mb:font-sm mb-1">
            <p class="mb-1">■注意事項</p>
            <p class="ml-6 mb-1">
              下記注意事項をすべて確認いただき、注意事項に同意した旨チェックボックスにチェックをいれてください
            </p>
          </div>
        </div>

        <!-- 注意事項 - 違約金-->
        {#if notesPenalty.isDisplay}
          <div class="inline-flex min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■違約金について</p>
                <div class="grid grid-cols-3 gap-2 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">
                      解約した場合 {hikari01Penalty?.penaltyAmount}円の違約金が発生します
                    </p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesPenalty.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div class="flex items-center pl-4 rounded border border-transparent {notesPenalty.bgColor}">
                  <input
                    id="notesPenaltyFlg"
                    type="checkbox"
                    bind:checked={notesPenalty.isChecked}
                    class="w-4 h-4 text-red-600"
                    disabled={notesPenalty.isDisabled}
                    on:change={notesPenalty.onChangeCheckbox}
                  />
                  <label for="notesPenaltyFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >違約金について同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-8 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- 注意事項 - キャンペーン違約金-->
        {#if notesCampaignPenalty.isDisplay}
          <div class="inline-flex min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■キャンペーン違約金について</p>
                <div class="grid grid-cols-3 gap-2 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">{contract.campaignName}</p>
                    <p class="ml-8">解約した場合9,500円の違約金が発生します</p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesCampaignPenalty.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div class="flex items-center pl-4 rounded border border-transparent {notesCampaignPenalty.bgColor}">
                  <input
                    id="notesCampaignPenaltyFlg"
                    type="checkbox"
                    bind:checked={notesCampaignPenalty.isChecked}
                    class="w-4 h-4 text-red-600"
                    disabled={notesCampaignPenalty.isDisabled}
                    on:change={notesCampaignPenalty.onChangeCheckbox}
                  />
                  <label for="notesCampaignPenaltyFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >キャンペーン違約金について同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-8 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- 注意事項 - 工事費残債-->
        {#if notesRemainingConstructionCost.isDisplay}
          <div class="inline-flex justify-items-center items-center min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■工事費残債について</p>
                <div class="grid grid-cols-3 gap-2 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">
                      解約した場合{hikari01Penalty.remainingConstructionCost}円の違約金が発生します
                    </p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesRemainingConstructionCost.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div
                  class="flex items-center pl-4 rounded border border-transparent {notesRemainingConstructionCost.bgColor}"
                >
                  <input
                    id="notesRemainingConstructionCostFlg"
                    type="checkbox"
                    bind:checked={notesRemainingConstructionCost.isChecked}
                    class="w-4 h-4 text-red-600"
                    disabled={notesRemainingConstructionCost.isDisabled}
                    on:change={notesRemainingConstructionCost.onChangeCheckbox}
                  />
                  <label
                    for="notesRemainingConstructionCostFlg"
                    class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >工事費残債について同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-8 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- 注意事項 - 設備の撤去工事残債-->
        {#if notesEquipmentRemovalWork.isDisplay}
          <div class="inline-flex justify-items-center items-center min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■設備の撤去工事について</p>
                <div class="grid grid-cols-3 gap-2 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">ご契約の設備は撤去工事が必要となります</p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesEquipmentRemovalWork.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div
                  class="flex items-center pl-4 rounded border border-transparent {notesEquipmentRemovalWork.bgColor}"
                >
                  <input
                    id="notesEquipmentRemovalWorkFlg"
                    type="checkbox"
                    bind:checked={notesEquipmentRemovalWork.isChecked}
                    class="w-4 h-4"
                    disabled={notesEquipmentRemovalWork.isDisabled}
                    on:change={notesEquipmentRemovalWork.onChangeCheckbox}
                  />
                  <label for="notesEquipmentRemovalWorkFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >撤去工事の必要性と日程調整について同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-12 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- 注意事項 - 回線モデムの返却 -->
        {#if notesReturnOfLineModem.isDisplay}
          <div class="inline-flex justify-items-center items-center min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■回線モデムの返却について</p>
                <div class="grid grid-cols-3 gap-2 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">回線モデムは返却が必要となります</p>
                    <p class="ml-8">期日までに返却いただけなかった場合、弁済費が発生します</p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesReturnOfLineModem.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div class="flex items-center pl-4 rounded border border-transparent {notesReturnOfLineModem.bgColor}">
                  <input
                    id="notesReturnOfLineModemFlg"
                    type="checkbox"
                    bind:checked={notesReturnOfLineModem.isChecked}
                    class="w-4 h-4 text-red-600"
                    disabled={notesReturnOfLineModem.isDisabled}
                    on:change={notesReturnOfLineModem.onChangeCheckbox}
                  />
                  <label for="notesReturnOfLineModemFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >回線モデムの返却、および未返却時の弁済費について同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-12 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- 注意事項 - いえのでんわ -->
        {#if notesWithdrawalOfTel.isDisplay}
          <div class="inline-flex justify-items-center items-center min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■いえのでんわの解約について</p>
                <div class="grid grid-cols-3 gap-2 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">
                      回線契約の解約により、付帯契約であるいえのでんわも解約となり、電話番号は使えなくなります
                    </p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesWithdrawalOfTel.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div class="flex items-center pl-4 rounded border border-transparent {notesWithdrawalOfTel.bgColor}">
                  <input
                    id="notesWithdrawalOfTelFlg"
                    type="checkbox"
                    bind:checked={notesWithdrawalOfTel.isChecked}
                    class="w-4 h-4 text-red-600"
                    disabled={notesWithdrawalOfTel.isDisabled}
                    on:change={notesWithdrawalOfTel.onChangeCheckbox}
                  />
                  <label for="notesWithdrawalOfTelFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >電話番号の取り扱い、機器返却、未返却時の弁済費に同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-12 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- 注意事項 - GateCall(F) / GateCall(M) の解約 -->
        {#if notesWithdrawalOfGateCall.isDisplay}
          <div class="inline-flex justify-items-center items-center min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■GateCall(F) / GateCall(M) の解約について</p>
                <div class="grid grid-cols-3 gap-2 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">
                      回線契約の解約により、付帯契約である電話契約も解約となり、電話番号は使えなくなります
                    </p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesWithdrawalOfGateCall.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div
                  class="flex items-center pl-4 rounded border border-transparent {notesWithdrawalOfGateCall.bgColor}"
                >
                  <input
                    id="notesWithdrawalOfGateCallFlg"
                    type="checkbox"
                    bind:checked={notesWithdrawalOfGateCall.isChecked}
                    class="w-4 h-4 text-red-600"
                    disabled={notesWithdrawalOfGateCall.isDisabled}
                    on:change={notesWithdrawalOfGateCall.onChangeCheckbox}
                  />
                  <label for="notesWithdrawalOfGateCallFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >電話番号の取り扱い、機器返却、未返却時の弁済費に同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-12 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- 注意事項 - メールアドレス -->
        {#if notesMailAddress.isDisplay}
          <div class="inline-flex justify-items-center items-center min-w-full mt-8">
            <div class="mb:font-sm mb-1 w-full">
              <div class="ml-6 mb-6">
                <p class="mb-1">■メールアドレスの取り扱い</p>
                <div class="grid grid-cols-3 gap-4 items-center">
                  <div class="col-span-2 justify-self-start">
                    <p class="ml-8">
                      回線契約の解約により、オプションとしてご利用いただいているメールアドレスも解約となり使えなくなります
                    </p>
                  </div>
                  <div class="justify-self-end">
                    <button class="button-primary" on:click={notesMailAddress.showModal}>詳細確認</button>
                  </div>
                </div>
              </div>
              <div class="ml-6">
                <div class="flex items-center pl-4 rounded border border-transparent {notesMailAddress.bgColor}">
                  <input
                    id="notesMailAddressFlg"
                    type="checkbox"
                    bind:checked={notesMailAddress.isChecked}
                    class="w-4 h-4 text-red-600"
                    disabled={notesMailAddress.isDisabled}
                    on:change={notesMailAddress.onChangeCheckbox}
                  />
                  <label for="notesMailAddressFlg" class="py-4 ml-2 w-full text-sm font-medium text-red-700"
                    >メールアドレスの取り扱いについて同意の上解約します</label
                  >
                </div>
                <div class="flex items-center">
                  <p class="text-xs font-normal ml-12 mt-2">
                    ※「詳細確認」ボタンを押下して、内容を確認の上チェックを付してください
                  </p>
                </div>
              </div>
            </div>
          </div>
        {/if}

        <!-- footer 連絡先 連絡先メールアドレス -->
        <div class="inline-flex justify-items-center items-center min-w-full mt-20">
          <div class="mb:font-sm mb-1 w-full">
            <p class="mb-1">解約にあたっての追加確認等のためお客様にご連絡が必要な場合発生します。</p>
            <p>
              以下に登録いただいているご連絡先に変更がないかご確認いただき、変更ある場合は、変更後の内容を入力ください
            </p>
          </div>
        </div>

        <div class="flex flex-col justify-items-center items-center mt-10">
          <div class="shadow rounded bg-gray-50 w-3/5 content-center p-4">
            <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
              <div class="grid-1 justify-self-start w-full">
                <span class="form-label-title required">連絡先電話番号</span>
              </div>
              <div class="grid-1 justify-self-start w-full">
                <input type="tel" maxlength="14" bind:value={contactTelNo} placeholder="" />
                {#if !contactTelNo}
                  <p class="text-red-500 text-xs mt-2">連絡先電話番号を入力してください</p>
                {/if}
                {#if contactTelNoErrorMessage}
                  <p class="text-red-500 text-xs mt-2">
                    {contactTelNoErrorMessage}
                  </p>
                {/if}
              </div>
            </div>

            <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
              <div class="grid-1 justify-self-start min-w-full">
                <span class="form-label-title">連絡先メールアドレス</span>
              </div>
              <div class="grid-1 justify-self-start w-full">
                <input type="email" bind:value={contactMailAddress} placeholder="" />
                {#if contactMailAddressErrorMessage}
                  <p class="text-red-500 text-xs mt-2">
                    {contactMailAddressErrorMessage}
                  </p>
                {/if}
              </div>
            </div>
            <div class="inline-grid grid-cols-2 items-center min-w-full p-2">
              <div class="grid-1 justify-self-start min-w-full">
                <span class="form-label-title">連絡先メールアドレス（再入力）</span>
              </div>
              <div class="grid-1 justify-self-start w-full">
                <input type="email" bind:value={contactMailAddressConfirmation} placeholder="" />
                {#if contactMailAddressConfirmationErrorMessage}
                  <p class="text-red-500 text-xs mt-2">
                    {contactMailAddressConfirmationErrorMessage}
                  </p>
                {/if}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col justify-items-center items-center">
        <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
          <button
            class="button-primary md:ml-5 mb-5"
            on:click={showWithdrawalCompleteModal}
            disabled={!isAllNotesChecked()}
          >
            手続きへ
          </button>
        </div>
      </div>
    </div>
  {/if}
{/await}

<style lang="postcss">
  .required:after {
    content: ' *';
    color: red;
  }
  .form-label-title {
    @apply text-sm font-medium text-gray-700;
  }
  li > p {
    @apply tracking-normal mb-1;
  }

  input[type='tel'],
  input[type='email'] {
    width: 60%;
    @apply rounded border py-2 px-3 text-sm placeholder-gray-400 focus:placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-transparent w-full;
  }
  .button-primary {
    @apply bg-green-400 rounded hover:bg-green-500 text-sm font-semibold text-white focus:outline-none px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
</style>
